/* eslint-disable @typescript-eslint/member-ordering */
import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Table } from 'primeng/table';
import { AppComponentName, ImportFrequency, ImportSchedule, Message, MessageActionType, MessageType, ImportItem, UserAccessRole, UserGroup, ImportScheduleInfo, ImportItemInfo, ImportDataCriteria, ImportItemDataType, ImportDataResultsFlat, SensorReadingType, ImportCSVFileContent, CSVFileAnalysisResults, ImportFeed, TimeZoneDisplay as TimeZoneSelection, ImportItemDetails, WPCrop, CompareSyncResults, SensorBasicsForCompareSync, CompareMcStats, SensorGroup } from '@models';
import { ChartData } from '@models/Dashboard/chartData.model';
import { ImportNowCriteria } from '@models/Import/importNowCriteria.model';
import { ImportSearchCriteria, ImportSearchMethod } from '@models/Import/ImportSearchCriteria.model';
import { ImportSearchResults, PortInfo } from '@models/Import/importSearchResults.model';
import { ImportService } from '@services/import.service';
import { AppMessageService } from '@services/message.service';
import { UserService } from '@services/user.service';
import { AppComponent } from 'app/app.component';
import { Authorization } from 'app/shared/classes/authorization';
import { CommonShared } from 'app/shared/classes/common';
import moment from 'moment';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationService } from 'primeng/api';
import * as d3 from 'd3';
import { Charting } from 'app/shared/classes/charting';
import { HubConnectionState } from '@microsoft/signalr';
import { Observable } from 'rxjs';
import { from, EMPTY } from 'rxjs';
import { HubConnectionFactory } from '../../shared/services/hub-connection-factory.service';
import { DateRanges } from '@models/ranges';
import { ImportLogsModalComponent, ImportLogsModalParams } from './import-logs-modal/import-logs-modal.component';
import { SensorGroupService } from '@services/sensorgroup.service';

interface DateRange {
  startDate: moment.Moment;
  endDate: moment.Moment;
}

@Component({
  standalone: false,
  selector: 'app-import',
  templateUrl: './import.component.html',
  styleUrls: ['./import.component.scss']
})

export class ImportComponent implements OnInit {

  public common = new CommonShared();
  public auth = new Authorization();
  private charting = new Charting();

  modalDragOn: boolean = false;

  public username = 'unknown';
  public userGroups: UserGroup[] = [];
  public associatedUserGroups: UserGroup[] = [];
  public selectedUserGroup: UserGroup;
  public importSchedules: ImportSchedule[] = [];
  public importSchedule: ImportSchedule;
  public importSchedulesReady: boolean = false;
  public associatedUserGroupsReady: boolean = false;
  public userGroupsReady: boolean = false;
  public globalSwitch: boolean;
  public deviceClass: string = "";
  public scheduleModalRef: BsModalRef;
  public scheduleImportNowRef: BsModalRef;
  public searchResultsRef: BsModalRef;
  public dataPreviewResultsRef: BsModalRef;
  public sampleCSVFormatRef: BsModalRef;
  public sensorResultsRef: BsModalRef;

  public scheduleSearchText: string;
  public preEditObjectAsJSONString = '';
  public duplicateScheduleName: boolean = false;
  public changeLogNotes: string = "";
  public importScheduleModalTitle: string = "";
  public importScheduleModalButtonTitle: string = "";
  public importSearchResults: ImportSearchResults[] = [];
  public searchItemLatitude: number;
  public searchItemLongitude: number;
  public searchItemRadius: number;
  public searchItemKey: string;
  public searchItemSecret: string;
  public importDataPreviewResults: ImportDataResultsFlat[] = [];
  public currentImportItem: ImportItem;
  public importNowIsLoading: boolean = false;
  public importNowCount: string;

  public csvFilename: string = '';
  public csvFileContent: string[][] = [];
  public csvFileContentShown: string[][] = [];
  public importCSVFileAnalysisResults: string;
  public analyzingCSVFile: boolean = false;
  public errorAnalyzingCSVFile: boolean = false;
  public parsedCSVFileContent: ImportCSVFileContent[] = [];
  public csvFileAnalysisResults: CSVFileAnalysisResults = {
    records: 0,
    uniqueUUIDs: 0,
    newUUIDs: 0,
    recordsWithGPS: 0,
    existingRecords: 0,
    numNewReadingTypes: 0,
    recordsImported: 0
  };

  public SourceFeedOptions: string[] = [];
  public WPCropOptions: string[] = [];
  public WPUseLocalTimeOptions: string[] = [ "true", "false" ];

  public invalidDates: moment.Moment[] = [];
  public selectedDateRange: DateRange = { startDate: moment(), endDate: moment() };
  public ranges = DateRanges;
  public sensorReadingTypesStartDate: string = moment().format('YYYY-MM-DD');

  public importScheduleCols = [
    {field: 'name', header: 'Schedule Name', width: '15%' },
    {field: 'description', header: 'Description', width: '20%' },
    {field: 'owner', header: 'Owner', width: '17%' },
    {field: 'frequency', header: 'Frequency', width: '10%' },
    {field: 'isDisabled', header: 'Enabled', width: '6%' },
    {field: 'modifiedDate', header: 'Modified Date', width: '15%' },
    {field: 'actionLog', header: 'Logs', width: '5%'},
    {field: 'copy', header: '', width: '4%' },
    {field: 'edit', header: '', width: '4%' },
    {field: 'del', header: '', width: '4%' }
  ];

  public importSearchResultWPCols = [
    {field: 'checked', header: 'Select', width: '4%' },
    {field: 'lastDataSetTimestamp', header: 'Last Reading Date', width: '18%' },
    {field: 'id', header: 'ID', width: '7%' },
    {field: 'name', header: 'Station Name', width: '15%' },
    {field: 'deviceType', header: 'Device Type', width: '9%' },
    {field: 'deviceName', header: 'Device Name', width: '10%' },
    {field: 'source', header: 'Source', width: '8%' },
    {field: 'latitude', header: 'Latitude', width: '9%' },
    {field: 'longitude', header: 'Longitude', width: '10%' },
    {field: 'distance', header: 'Distance', width: '10%' }
  ];
  
    public importSearchResultWLCols = [
    {field: 'checked', header: 'Select', width: '4%' },
      {field: 'thingId', header: 'ID', width: '7%' },
      {field: 'thingName', header: 'Station Name', width: '15%' },
    {field: 'gateway_Type', header: 'Device Type', width: '15%' },
    {field: 'city', header: 'City', width: '10%' },
    {field: 'region', header: 'Region', width: '7%' },
    {field: 'latitude', header: 'Latitude', width: '10%' },
    {field: 'longitude', header: 'Longitude', width: '10%' },
    {field: 'subscription_Type', header: 'Subscription', width: '7%' },
    {field: 'user_Email', header: 'Email', width: '17%' }
  ];

  public importSearchResultSenTekCols = [
    {field: 'checked', header: 'Select', width: '6%' },
    {field: 'lastReportDate', header: 'Last Reading Date', width: '26%' },
    {field: 'thingId', header: 'ID', width: '14%' },
    {field: 'thingName', header: 'Name', width: '18%' },
    {field: 'latitude', header: 'Latitude', width: '18%' },
    {field: 'longitude', header: 'Longitude', width: '18%' },
  ];

    public importSearchResultIRROCloudCols = [
    {field: 'checked', header: 'Select', width: '6%' },
    {field: 'lastReportDate', header: 'Last Reading Date', width: '26%' },
    {field: 'thingId', header: 'ID', width: '14%' },
    {field: 'thingName', header: 'Name', width: '18%' },
    {field: 'latitude', header: 'Latitude', width: '18%' },
    {field: 'longitude', header: 'Longitude', width: '18%' },
  ];

  public importSearchResultSoiltechCols = [
    {field: 'checked', header: 'Select', width: '8%' },
    {field: 'id', header: 'ID', width: '30%' },
    {field: 'name', header: 'Name', width: '50%' }
  ];

  public importDataPreviewResultCols = [
    {field: 'timeStamp', header: 'Time Stamp', width: '33%' },
    {field: 'dataType', header: 'Reading Type', width: '33%' },
    {field: 'value', header: 'Value', width: '33%' }
  ];

  public compareMcSensorResultsTableCols = [
    {field: 'uid', header: 'UID', width: '17%' },
    {field: 'name', header: 'Name', width: '13%' },
    {field: 'product_id', header: 'Product Id', width: '8%' },
    {field: 'product_name', header: 'Device Type', width: '11%' },
    {field: 'group_name', header: 'Groups', width: '16%' },
    {field: 'kigNew', header: 'Kig-New', width: '4%' },
    {field: 'kigNameUpdate', header: 'Kig-Name', width: '4%' },
    {field: 'kigProdIdUpdate', header: 'Kig-ProdId', width: '4%' },
    {field: 'kigGroupUpdate', header: 'Kig-Group', width: '4%' },
    {field: 'particleNew', header: 'Par-New', width: '4%' },
    {field: 'particleNameUpdate', header: 'Par-Name', width: '4%' },
    {field: 'particleProdIdUpdate', header: 'Par-ProdId', width: '4%' },
    {field: 'particleGroupUpdate', header: 'Par-Group', width: '4%' },
    {field: '', header: 'Sync', width: '3%' },
  ];

  logConfig = {
    size: 'lg',
    backdrop: false,
    ignoreBackdropClick: false
  };

  public userGroupConfig: any = {
    displayKey: 'name',
    search: true,
    height: '300px',
    placeholder: 'Select User Group',
    noResultsFound: 'No User Groups found!',
    searchOnKey: 'name',
    limitTo: 0
  };

  public dataSourceOptions: string[] = [ "", "Weather Pillar", "Weather Link", "SenTek", "Soiltech", "Zentra", "IRROCloud","CSV File" ]; // Following only for Admins "Maintenance Connection Sync" ];

  public dataSourceConfig: any = {
    displayKey: 'name',
    search: false,
    height: '300px',
    placeholder: 'Select Data Source',
    noResultsFound: 'No matching data sources found!',
    searchOnKey: 'name',
    limitTo: 0
  };

  public detailsConfig: any = {
    displayKey: 'name',
    search: false,
    width: '150px',
    height: '300px',
    placeholder: 'Select',
    noResultsFound: 'No options found!',
    searchOnKey: 'name',
    limitTo: 0
  };

  public datatypeDropDownSettings: any = {
    displayKey: 'name',
    search: true,
    height: '300px',
    placeholder: 'Select Reading Type(s)',
    noResultsFound: 'No results found',
    searchOnKey: 'name',
    searchPlaceholder: 'Search',
    limitTo: 0
  };

  public timeStampDropDownSettings: any = {
    displayKey: 'name',
    search: true,
    height: '300px',
    placeholder: 'Select',
    noResultsFound: 'No results found',
    searchOnKey: 'name',
    searchPlaceholder: 'Search',
    limitTo: 0
  };

  public readingTypeKeyRef: BsModalRef;
  public getDataForThingLocked: boolean;
  public chartModalRef: BsModalRef;
  public chart: ChartData[] = [];
  public dataTypes: SensorReadingType[] = [];
  public view: any[] = [1000,700];
  public chartOptions: any = {
    showXAxis: true,
    showYAxis: true,
    gradient: true,
    showLegend: true,
    animations: false,
    showXAxisLabel: true,
    xAxisLabel: 'Date-Time (Select range from the below scale)',
    showYAxisLabel: true,
    yAxisLabel:'Value',
    autoScale: true,
    legendTitle:'Charted Readings',
    timeline: true,
    colorScheme: {
      domain: [ '#046dd6', '#800080', '#42f55a', '#ffc107', '#fa0f0f' ]
    }
  };

  private svgDataAvailability;
  private dataAvailabilityMargin = 20;
  private dataAvailabilityWidth = 1455 - (this.dataAvailabilityMargin * 2);
  private dataAvailabilityHeight = 60 - (this.dataAvailabilityMargin * 2);
  private sensorDataAvailability: Date[] = [];

  private hubConnection: signalR.HubConnection;
  public lastImportJobStatusMessage: string = "";
  public recentImportJobStatusMessages: string[] = [];
  public importUpdateDisplayTimer: any;

  public selectedTimeZone: TimeZoneSelection = TimeZoneSelection.Local;
  public selectedTimeZoneOffset: number = moment().utcOffset();


  constructor(private modalService: BsModalService, public toastr: ToastrService, public _userService: UserService,
              public global: AppComponent, private confirmationService: ConfirmationService, public _sensorGroupService: SensorGroupService,
              public _appMessageService: AppMessageService, public _importService: ImportService,
              private hubFactory: HubConnectionFactory, private route: ActivatedRoute) {

    this.globalSwitch = global.globalSwitch;
    this.linkedSearch = route.snapshot.queryParamMap.get('search');

    this.hubConnection = this.hubFactory.create('/api/hubs/import');
    this.hubConnection.onclose(error => console.log('hubConnection.onclose') );
    this.hubConnection.onreconnecting(error => console.log('Attempting reconnect...') );
    this.hubConnection.onreconnected(() => console.log('Reconnected.') );
    this.hubConnection.on('refreshImport', (updateType: string, updatedImport: string) => this.refreshImport(updateType, updatedImport));
  }


  ngOnInit() {
    this._userService.getCurrentUserEmailUsingAzureAD().subscribe(
      results => {
        this.username = results['value'];
        if (this.username != null && this.username != '') {
          this.startUp();
        }
      },
      err => {
        this.toastr.error('Error unable to read current user\'s info');
      });
  }


  startUp() {
    this.loadSavedSettings();
    this.startSignalR();
    this.getAssociatedUserGroups();
    // this.setupWPDropdownOptions();
    // this.setupWLDropdownOptions();
    this.updateCompareMcTableColsNames();
    //this.updateDropdownContent();
  }


  setDragOn(drag: boolean) {
    this.modalDragOn = drag;

    if (drag) {
      setTimeout(() => {
        this.modalDragOn = false;
      },3000);
    }
  }


  updateDropdownContent() {
    if (this.importSchedule.dataSource == "Weather Link") {
      this.setupWLDropdownOptions();
    }
    else if (this.importSchedule.dataSource == "Weather Pillar") {
      this.setupWPDropdownOptions();
    }
  }


  updateCompareMcTableColsNames() {
    if (this.global.appName.toLowerCase() != 'kig') {
      this.compareMcSensorResultsTableCols = [
        {field: 'uid', header: 'UID', width: '17%' },
        {field: 'name', header: 'Name', width: '13%' },
        {field: 'product_id', header: 'Product Id', width: '8%' },
        {field: 'product_name', header: 'Product Name', width: '11%' },
        {field: 'group_name', header: 'Group Name', width: '16%' },
        {field: 'kigNew', header: 'Dots-New', width: '4%' },
        {field: 'kigNameUpdate', header: 'Dots-Name', width: '4%' },
        {field: 'kigProdIdUpdate', header: 'Dots-ProdId', width: '4%' },
        {field: 'kigGroupUpdate', header: 'Dots-Group', width: '4%' },
        {field: 'particleNew', header: 'Par-New', width: '4%' },
        {field: 'particleNameUpdate', header: 'Par-Name', width: '4%' },
        {field: 'particleProdIdUpdate', header: 'Par-ProdId', width: '4%' },
        {field: 'particleGroupUpdate', header: 'Par-Group', width: '4%' },
        {field: '', header: 'Sync', width: '3%' }
      ];
    }
  }


  updateDatasourceOptions() {
    var adminsGroup = this.userGroups.find(x => x.name.toLowerCase() == 'admins');
    if (adminsGroup != undefined) {
      var isUserInAdmins = adminsGroup.userInfos.find(x => x.userName == this.username);

      if (isUserInAdmins != undefined) {
        this.dataSourceOptions = [ "", "Weather Pillar", "Weather Link", "SenTek", "Soiltech", "Zentra", "IRROCloud", "CSV File", "Maintenance Connection Sync" ];
      }
    }
  }


  loadSavedSettings() {
    [this.selectedTimeZone, this.selectedTimeZoneOffset] = this.common.setTimeZoneSelection(localStorage.getItem('TimeZoneSelected'));
  }


  setupWPDropdownOptions() {
    this.WPCropOptions = [];

    for(var prop in WPCrop) {
      this.WPCropOptions.push(WPCrop[prop]);
    }

    this.SourceFeedOptions = [];

    // for(var prop in ImportFeed) {
    //   if (prop != 'DailySum') {
    //     this.SourceFeedOptions.push(ImportFeed[prop]);
    //   }
    // }

    this.SourceFeedOptions.push("SubDaily");
    this.SourceFeedOptions.push("DailyAvgLL");
    this.SourceFeedOptions.push("DailyObsLL");
    this.SourceFeedOptions.push("DailyObsSta");
  }


  setupWLDropdownOptions() {
    this.SourceFeedOptions = [];
    this.SourceFeedOptions.push("Current");
    this.SourceFeedOptions.push("Historical");
  }


  private startSignalR(): Observable<any> {
    return (this.hubConnection.state === HubConnectionState.Disconnected) ? from(this.hubConnection.start()) : EMPTY;
  }

  public ngOnDestroy() { this.stopSignalR(); }

  private stopSignalR() { this.hubConnection.stop(); }

  private refreshImport(updateType: string, updatedImport: string) {
    this.refreshImportStatus(updateType, updatedImport);
  }

  public refreshImportStatus(updateType: string, updateMessage: string) {
    this.lastImportJobStatusMessage = updateMessage;
    this.storeImportJobStatusMessage(`${moment().format("yyyy-mm-dd HH:MM")}  ${this.lastImportJobStatusMessage}`);
    this.startTimer4SensorGroupUpdateDisplay();
  }


  startTimer4SensorGroupUpdateDisplay() {
    if (this.importUpdateDisplayTimer) {
      clearInterval(this.importUpdateDisplayTimer);
    }
    this.importUpdateDisplayTimer = setInterval(() => {
      this.lastImportJobStatusMessage = "";
      clearInterval(this.importUpdateDisplayTimer);
    }, 120000);
  }


  storeImportJobStatusMessage(message: string) {
    var maxHistorySize = 50;
    this.recentImportJobStatusMessages.push(`${moment().format("YYYY-MM-DD  h:mm a")}  ${this.lastImportJobStatusMessage}`);

    if (this.recentImportJobStatusMessages.length > maxHistorySize) {
      this.recentImportJobStatusMessages.shift();
    }
  }


  getRecentImportJobMessages(): string {
    var allMessages: string = "";
     var newArray = [...this.recentImportJobStatusMessages];
    return newArray.reverse().join('\n');
  }


  getUserGroups(): void {
    this.userGroupsReady = false;
    this._userService.getUserGroups().subscribe(
      results => {
        if (results != null) {
          this.userGroups = results;
          this.userGroupsReady = true;
          this.getImportSchedules();
          this.updateDatasourceOptions();
        } else {
          this.toastr.warning('No user groups found');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while retrieving schedules');
      });
  }


  getAssociatedUserGroups(): void {
    this.associatedUserGroupsReady = false;
    this._userService.getAssociatedUserGroups(this.username).subscribe(
      results => {
        if (results != null) {
          this.associatedUserGroups = results;
          this.getUserGroups();
          this.associatedUserGroupsReady = true;
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while retrieving current display.');
      });
  }


  getImportSchedules() {
    this._importService.getImportSchedules().subscribe(
      results => {
        if (results != null) {
          this.importSchedules = this.convertImportScheduleInfos2ImportSchedules(results);
          this.importSchedulesReady = true;
        } else {
          this.toastr.warning('Error retrieving schedules');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while retrieving schedules');
      });
  }


  convertImportScheduleInfos2ImportSchedules(importScheduleInfos: ImportScheduleInfo[]): ImportSchedule[] {
    var importSchedules: ImportSchedule[] = [];

    importScheduleInfos.forEach(x => {
      importSchedules.push(this.convertImportScheduleInfo2ImportSchedule(x));
    });

    return importSchedules;
  }


  convertImportScheduleInfo2ImportSchedule(importScheduleInfo: ImportScheduleInfo): ImportSchedule {
    var x = importScheduleInfo;
    var importSchedule: ImportSchedule = {
      id: x.id,
      name: x.name,
      description: x.description,
      dataSource: x.dataSource,
      isDisabled: x.isDisabled,
      importItems: this.convertImportItemInfos2ImportItems(x.importItems),
      userName: x.userName,
      userGroupId: x.userGroupId,
      modifiedDate: moment.utc(x.modifiedDate).local().toDate(),
      frequency: this.getFrequencyValueAtIndex(x.frequency),
      freqValue: x.freqValue
    }

    this.auth.mapUserAccessForImports(importSchedule, this.globalSwitch, this.username, this.userGroups);
    importSchedule.isDisabledColor = this.getEnabledColor(x.isDisabled);
    importSchedule.displayedOwner = (x.userGroupId == null) ? x.userName : this.userGroups.find(y => y.id == x.userGroupId).name;

    return importSchedule;
  }


  convertImportSchedules2ImportScheduleInfos(importSchedules: ImportSchedule[]): ImportScheduleInfo[] {
    var importScheduleInfos: ImportScheduleInfo[] = [];

    importSchedules.forEach(x => {
      importScheduleInfos.push(this.convertImportSchedule2ImportScheduleInfo(x));
    });

    return importScheduleInfos;
  }


  convertImportSchedule2ImportScheduleInfo(importSchedule: ImportSchedule): ImportScheduleInfo {
    var x = importSchedule;
    var importScheduleInfo: ImportScheduleInfo = {
      id: x.id,
      name: x.name,
      description: x.description,
      dataSource: x.dataSource,
      isDisabled: x.isDisabled,
      importItems: this.convertImportItems2ImportItemInfos(x.importItems),
      userName: x.userName,
      userGroupId: x.userGroupId,
      modifiedDate: x.modifiedDate,
      frequency: Object.values(ImportFrequency).indexOf(x.frequency),
      freqValue: x.freqValue
    }

    return importScheduleInfo;
  }


  convertImportItemInfos2ImportItems(importItemInfos: ImportItemInfo[]): ImportItem[] {
    var importItems: ImportItem[] = [];

    importItemInfos.forEach(x => {
      importItems.push(this.convertImportItemInfo2ImportItem(x));
    });

    return importItems;
  }


  parseImportItemDetails(detailsAsJson: string): ImportItemDetails {
    var itemDetails: ImportItemDetails;

    try {
      itemDetails = JSON.parse(detailsAsJson);  
    }
    catch (error) {
      itemDetails = {
        nodeName: detailsAsJson,
        nodeType: '',
        port_number: 0,
        product_name: '',
        manufacturer: '',
        lsid: 0,
        crop: WPCrop.corn,
        useLocalTime: 'true',
        city: '',
        region: '',
        country: '',
        gateway_Type: '',
        station_Id_Uuid: '',
        user_Email: '',
        subscription_Type: ''
      };
    }

    return itemDetails;
  }


  convertImportItemInfo2ImportItem(importItemInfo: ImportItemInfo): ImportItem {
    var x = importItemInfo;
    var importItem: ImportItem = {
      id: x.id,
      thingName: x.thingName,
      details: this.parseImportItemDetails(x.details),
      dataTypesSelected: [],
      dataTypeOptions: [],
      importScheduleId: x.importScheduleId,
      timeStampParm: { id: 0, name: x.timeStampParm },
      feed: this.getFeedValueAtIndex(x.feed),
      latitudeDaily: x.latitudeDaily,
      longitudeDaily: x.longitudeDaily,
      radiusDaily: x.radiusDaily,
      refId: x.refId,
      key: x.key,
      compareSyncResults: undefined,
      compareStats: undefined
    }
    this.initializeCompareSyncStatResults(importItem);

    if (importItemInfo.dataTypes != undefined) {
      var infoDataTypes = importItemInfo.dataTypes.split(',');
      infoDataTypes.forEach(x => {
        importItem.dataTypesSelected.push( { id: this.generateRandomPositiveID() , name: x } );
      });
    }

    return importItem;
  }


  convertImportItems2ImportItemInfos(importItems: ImportItem[]): ImportItemInfo[] {
    var importItemInfos: ImportItemInfo[] = [];

    importItems.forEach(x => {
      importItemInfos.push(this.convertImportItem2ImportItemInfo(x));
    });

    return importItemInfos;
  }


  convertImportItem2ImportItemInfo(importItem: ImportItem): ImportItemInfo {
    var x = importItem;
    var importItemInfo: ImportItemInfo = {
      id: x.id,
      thingName: x.thingName,
      details: JSON.stringify(x.details),
      dataTypes: (x.dataTypesSelected != undefined && x.dataTypesSelected.length > 0) ? x.dataTypesSelected.map(y => y.name).join(',') : undefined,
      importScheduleId: x.importScheduleId,
      timeStampParm: x.timeStampParm.name,
      feed: Object.values(ImportFeed).indexOf(x.feed),
      latitudeDaily: x.latitudeDaily,
      longitudeDaily: x.longitudeDaily,
      radiusDaily: x.radiusDaily,
      refId: x.refId,
      key: x.key
    }

    return importItemInfo;
  }


  getFrequencyValueAtIndex(index: any): ImportFrequency {
    const enumString = (index >= 0) ? Object.values(ImportFrequency)[index].toLowerCase() : '';
    switch (enumString) {
    case 'daily':
      return ImportFrequency.Daily;
    case 'hourly':
      return ImportFrequency.Hourly;
    case 'every30min':
      return ImportFrequency.Every30Min;
    case 'every15min':
      return ImportFrequency.Every15Min;
    case 'once':
      return ImportFrequency.Once;
    default:
      return ImportFrequency.Daily;
    }
  }


  getFeedValueAtIndex(index: any): ImportFeed {
    const enumString = (index >= 0) ? Object.values(ImportFeed)[index] : '';

    switch (enumString) {
    case 'SubDaily':
      return ImportFeed.SubDaily;
    case 'DailyAvgLL':
      return ImportFeed.DailyAvgLL;
    case 'DailyObsLL':
      return ImportFeed.DailyObsLL;
    // case 'DailyAvgSta':
    //   return WPImportFeed.DailyAvgSta;
    case 'DailyObsSta':
      return ImportFeed.DailyObsSta;
    case "Current":
      return ImportFeed.Current;
    case "Historical":
      return ImportFeed.Historical;
    default:
      return ImportFeed.SubDaily;
    }
  }


  newImportSchedule(): ImportSchedule {
    var schedule: ImportSchedule = {
      id: 0,
      name: "",
      description: "",
      displayedOwner: this.username,
      frequency: ImportFrequency.Daily,
      freqValue: 0,
      isDisabled: true,
      modifiedDate: moment().toDate(),
      userName: this.username,
      userGroupId: null,
      dataSource: "",
      importItems: []
    }

    return schedule;
  }


  getEnabledColor(isDisabled: boolean): string {
    if (isDisabled == false) {
      return '#a9d18e';
    } else {
      return 'clear';
    }
  }


  addNewSchedule(template: TemplateRef<any>) {
    this.searchItemKey = "";
    this.searchItemSecret = "";
    this.importScheduleModalTitle = "New Schedule";
    this.importScheduleModalButtonTitle = "Create";
    this.clearScheduleValues();

    this.importSchedule = this.newImportSchedule();
    this.importSchedule.importItems.forEach(x => { x.isLoadingDataTypes == false; x.isLoadingItemData == false; });
    this.selectedUserGroup = this.associatedUserGroups.find(u => u.id == -1);
    this.auth.mapUserAccessForImports(this.importSchedule, this.globalSwitch, this.username, this.userGroups);

    this.importCSVFileAnalysisResults = "";
    this.parsedCSVFileContent = [];

    this.lastImportJobStatusMessage = "";

    this.scheduleModalRef = this.modalService.show(template, { animated: false, backdrop: false, ignoreBackdropClick: true } );
  }


  editExistingSchedule(schedule: ImportSchedule, template: TemplateRef<any>) {
    this.searchItemKey = "";
    this.searchItemSecret = "";
    this.importScheduleModalTitle = "Edit Schedule";
    this.importScheduleModalButtonTitle = "Update";
    this.clearScheduleValues();

    this.importSchedule = this.cloneSchedule(schedule);
    this.preEditObjectAsJSONString = JSON.stringify(schedule);
    this.importSchedule.importItems.forEach(x => { x.isLoadingDataTypes == false; x.isLoadingItemData == false; });

    if (schedule.userGroupId == null) {
      this.selectedUserGroup = this.associatedUserGroups.find(u => u.id == -1);
    } else {
      this.selectedUserGroup = this.associatedUserGroups.find(u => u.id == schedule.userGroupId);
    }

    if ((schedule.dataSource == "Zentra" || schedule.dataSource == "SenTek" || schedule.dataSource == "Maintenance Connection" || schedule.dataSource == "IRROCloud") && schedule.importItems.length > 0) {
      this.searchItemKey = schedule.importItems[0].key;
    }
    else if (schedule.dataSource == "Weather Link" && schedule.importItems.length > 0) {
      try {
        var keyObj = JSON.parse(schedule.importItems[0].key);
        this.searchItemKey = keyObj.key;
        this.searchItemSecret = keyObj.secret;
      } catch (e) {
        this.searchItemKey = schedule.importItems[0].key;
        this.searchItemSecret = "";
      }
    }

    this.updateDropdownContent();

    this.lastImportJobStatusMessage = "";

    this.scheduleModalRef = this.modalService.show(template, { animated: false, backdrop: false, ignoreBackdropClick: true } );
  }


  clearScheduleValues() {
    this.changeLogNotes = '';

    this.csvFileContent = [];
    this.csvFileContentShown  = [];
    this.importCSVFileAnalysisResults = "";
    this.analyzingCSVFile = false;
    this.parsedCSVFileContent = [];
    this.csvFileAnalysisResults = {
      records: 0,
      uniqueUUIDs: 0,
      newUUIDs: 0,
      recordsWithGPS: 0,
      existingRecords: 0,
      numNewReadingTypes: 0,
      recordsImported: 0
    };
  }


  toggleImportEnableState(importSchedule: ImportSchedule) {
    this.importSchedule = importSchedule;

    this.preEditObjectAsJSONString = JSON.stringify(importSchedule);
    this.importSchedule.importItems.forEach(x => { x.isLoadingDataTypes == false; x.isLoadingItemData == false; });

    if (importSchedule.userGroupId == null) {
      this.selectedUserGroup = this.associatedUserGroups.find(u => u.id == -1);
    } else {
      this.selectedUserGroup = this.associatedUserGroups.find(u => u.id == importSchedule.userGroupId);
    }

    this.importSchedule.isDisabled = !importSchedule.isDisabled;
    this.saveSchedule();
  }


  saveSchedule() {
    this.updateSensorName(this.importSchedule);
    this.updateScheduleUserInfo(this.importSchedule);

    if (!this.checkMinimumScheduleRequirements(this.importSchedule)) {
      return;
    }

    if (this.scheduleModalRef != undefined) {
      this.scheduleModalRef.hide();
    }

    this.importSchedule.importItems.forEach(x => {
      if (x.id < 0) x.id = 0;
      
      if (this.importSchedule.dataSource == "Weather Link") {
         x.key =  JSON.stringify({ "key": this.searchItemKey, "secret": this.searchItemSecret });
      }
      else {
          x.key = this.searchItemKey;
      }
    });

    var importScheduleInfo = this.convertImportSchedule2ImportScheduleInfo(this.importSchedule);

    // NEW SCHEDULE
    if (this.importSchedule.id < 1) {
      this._importService.createImportSchedule(importScheduleInfo).subscribe(
        results => {
          if (results != null) {
            this.createMessageData(MessageActionType.New, this.importSchedule, '');
            this.getImportSchedules();
            this.toastr.success('Schedule created');
          } else {
            this.toastr.warning('Error saving new schedule');
          }
          return;
        },
        err => {
          this.toastr.error('Error occurred while saving new schedule');
        });
    }
    // UPDATE SCHEDULE
    else {
      this._importService.updateImportSchedule(importScheduleInfo, this.username).subscribe(
        results => {
          if (results != null) {
            this.createMessageData(MessageActionType.Edited, this.importSchedule, this.getEditChanges(this.importSchedule));
            this.getImportSchedules();
            this.toastr.success('Schedule updated');
          } else {
            this.toastr.warning('Error updating schedule');
          }
          return;
        },
        err => {
          this.toastr.error('Error occurred while saving edited schedule');
        });
    }
  }


  updateScheduleUserInfo(schedule: ImportSchedule) {
    if (this.selectedUserGroup != null && this.selectedUserGroup.id > -1) {
      schedule.userGroupId = this.selectedUserGroup.id;
      schedule.displayedOwner = this.selectedUserGroup.name;
    }
    else {
      schedule.displayedOwner = this.username;
      schedule.userGroupId = null;
    }
  }


  checkMinimumScheduleRequirements(schedule: ImportSchedule): boolean {
    let verified = true;

    if (schedule.name == undefined) {
      this.toastr.error('Please provide a name for the schedule');
      verified = false;
    }

    if (this.duplicateScheduleNameCheck(schedule.name)) {
      verified = false;
    }

    return verified;
  }


  copySchedule(schedule: ImportSchedule) {
    let copyIndex: number;
    for (copyIndex = 1; copyIndex < 500; copyIndex++) {
      const results = this.importSchedules.findIndex(t => t.name == schedule.name + ' (copy ' + copyIndex + ')');
      if (results == -1) {
        break;
      }
    }

    const newSchedule = Object.assign({}, schedule);
    newSchedule.id = 0;
    newSchedule.isDisabled = true;
    newSchedule.isDisabledColor = this.getEnabledColor(newSchedule.isDisabled);
    newSchedule.name = schedule.name + ' (copy ' + copyIndex + ')';
    newSchedule.importItems.forEach(x => x.id = 0);
    newSchedule.modifiedDate = moment().toDate();

    var importScheduleInfo = this.convertImportSchedule2ImportScheduleInfo(newSchedule);

    this._importService.createImportSchedule(importScheduleInfo).subscribe(
      results => {
        if (results != null) {
          this.createMessageData(MessageActionType.New, schedule, '');
          this.getImportSchedules();
          this.toastr.success('Schedule copied');
        } else {
          this.toastr.warning('Error copying schedule');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while copying schedule');
      });
  }


  onDeleteConfirm(schedule: ImportSchedule): void {
    this.confirmationService.confirm({
      message: 'Do you want to delete this schedule?',
      header: 'Delete Confirmation',
      icon: 'glyphicon glyphicon-warning-sign',
      accept: () => { this.deleteSchedule(schedule); },
      reject: () => { }
    });
  }


  deleteSchedule(schedule: ImportSchedule) {
    this._importService.deleteImportSchedule(schedule.id, this.username).subscribe(
      results => {
        if (results != null) {
          this.createMessageData(MessageActionType.Deleted, schedule, '');
          var scheduleIndex = this.importSchedules.findIndex(x => x.id == schedule.id);
          this.importSchedules.splice(scheduleIndex, 1);
          this.importSchedules = [...this.importSchedules];
          this.toastr.success('Schedule removed');
        } else {
          this.toastr.warning('Error removing schedule');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while removing schedule');
      });
  }


  searchSchedulesTextChanged() {
  }


  // Search text passed in by a link (e.g. from the home page search). The schedules table only exists
  // once its data has loaded, so the text is applied when the table appears.
  private linkedSearch: string | null = null;

  @ViewChild('importTable') set importTableRef(table: Table | undefined) {
    if (table == undefined || this.linkedSearch == null) return;

    const text = this.linkedSearch;
    this.linkedSearch = null;
    // Deferred so the search box's value is not changed in the middle of a change-detection pass
    setTimeout(() => {
      this.scheduleSearchText = text;
      table.filterGlobal(text, 'contains');
    });
  }


  onScheduleEnable(value: any) {
    this.importSchedule.isDisabled = !value.checked;
    this.importSchedule.isDisabledColor = this.getEnabledColor(this.importSchedule.isDisabled);
  }


  duplicateScheduleNameCheck(name: string): boolean {
    this.duplicateScheduleName = false;
    this.importSchedules.forEach(e => {
      if (e.name.toUpperCase() == name.toUpperCase() && e.id != this.importSchedule.id){
        this.toastr.error('Name already being used');
        this.duplicateScheduleName = true;
        return this.duplicateScheduleName;
      }
    });
    return this.duplicateScheduleName;
  }


  searchForItems(template: TemplateRef<any>, includeNodes: boolean) {
    var searchCriteria: ImportSearchCriteria = {
      dataSource: this.importSchedule.dataSource,
      searchMethod: ImportSearchMethod.Location,
      latitude: this.searchItemLatitude,
      longitude: this.searchItemLongitude,
      radius: this.searchItemRadius,
      name: '',
      id: '',
      key: this.searchItemKey,
      secret: this.searchItemSecret,
      includeNodes: includeNodes
    }

    this._importService.searchForThings(searchCriteria).subscribe(
      results => {
        if (results != null) {
          this.importSearchResults = results;
          this.searchResultsRef = this.modalService.show(template, { animated: false, backdrop: false, ignoreBackdropClick: true } );

          if (this.importSchedule.dataSource == "Weather Pillar") {
            this.importSearchResults.forEach(x => {
              x.lastReportDate = new Date(x.lastDataSetTimestamp);
              x.lastReportColor = this.common.getColorCodeForLastReadingDate(x.lastReportDate);
              x.parentTooltips = '';

              if (x.portInfos != undefined && x.portInfos.length > 0) {
                x.portInfos.forEach(y => {
                  x.parentTooltips += `Port: ${y.port_number}, ${y.manufacturer}, ${y.product_name}\n`;
                });
              } 
            });
          }

          if (this.importSchedule.dataSource == "IRROCloud") {
            this.importSearchResults.forEach(x => {
              x.lastReportDate = moment.utc(x.lastDataSetTimestamp).local().toDate();
              x.lastReportColor = this.common.getColorCodeForLastReadingDate(x.lastReportDate);
            });
          }

          if (this.importSchedule.dataSource == "SenTek" || this.importSchedule.dataSource == "Soiltech") {
            this.importSearchResults.forEach(x => {
              this.getLastReadingDate(searchCriteria.dataSource, x);
            });
          }

        } else {
          this.toastr.warning('Error searching for things');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while search for things');
    });
  }


  getLastReadingDate(dataSource: string, importSearchResult: ImportSearchResults) {
    var searchCriteria: ImportSearchCriteria = {
      dataSource: dataSource,
      searchMethod: ImportSearchMethod.Name,
      latitude: 0,
      longitude: 0,
      radius: 0,
      name: importSearchResult.thingName,
      id: importSearchResult.thingId.toString(),
      key: this.searchItemKey,
      secret: this.searchItemSecret,
      includeNodes: false
    }

    importSearchResult.isLoadingLastReading = true;
   
    this._importService.getLastReadingDate(searchCriteria).subscribe(
      results => {
        importSearchResult.isLoadingLastReading = false;

        if (results != null) {
          var dateResult: ImportSearchResults = results;

          importSearchResult.lastReportDate = new Date(dateResult.lastDataSetTimestamp);
          importSearchResult.lastReportColor = this.common.getColorCodeForLastReadingDate(importSearchResult.lastReportDate);
        }
      },
      err => {
        importSearchResult.isLoadingLastReading = false;
        this.toastr.error('Error retrieving last reading date for ' + searchCriteria.name);
    });
  }


  importCheckedThings() {
    var importItem: ImportItem;

    this.importSearchResults.forEach(x => {
      if (x.checked) {
        if (this.importSchedule.dataSource == 'Weather Pillar') {
          if (x.portInfos == undefined || x.portInfos.length < 1) {
            const importItemDetails = this.createImportItemDetailsFromSearchResults(x, this.importSchedule.dataSource, undefined);
            importItem = this.addImportItem(x.thingId.toString(), x.thingId.toString(), x.latitude, x.longitude, this.searchItemKey, importItemDetails);
            this.getDataTypesForThing(importItem, this.importSchedule.dataSource);
          }
          else {
            x.portInfos.forEach(node => {
              const importItemDetails = this.createImportItemDetailsFromSearchResults(x, this.importSchedule.dataSource, node);
              importItem = this.addImportItem(x.thingId.toString(), x.thingId.toString(), x.latitude, x.longitude, this.searchItemKey, importItemDetails);
              this.getDataTypesForThing(importItem, this.importSchedule.dataSource);
            });
          }
          
        }

        else if (this.importSchedule.dataSource == 'Weather Link') {
          const importItemDetails = this.createImportItemDetailsFromSearchResults(x, this.importSchedule.dataSource, undefined);
          var keyObject = JSON.stringify({ "key": this.searchItemKey, "secret": this.searchItemSecret });
          importItem = this.addImportItem(x.thingId.toString(), x.thingName, x.latitude, x.longitude, keyObject, importItemDetails);
          importItem.feed = (importItemDetails.subscription_Type != undefined && importItemDetails.subscription_Type.toLowerCase() == 'basic') ? ImportFeed.Current : ImportFeed.Historical;
          this.getDataTypesForThing(importItem, this.importSchedule.dataSource);
        }

        else if (this.importSchedule.dataSource == 'SenTek' || this.importSchedule.dataSource == 'Soiltech' || this.importSchedule.dataSource == 'IRROCloud') {
          const importItemDetails = this.createImportItemDetailsFromSearchResults(x, this.importSchedule.dataSource, undefined);
          importItem = this.addImportItem(x.thingId.toString(), x.thingName, x.latitude, x.longitude, this.searchItemKey, importItemDetails);
          this.getDataTypesForThing(importItem, this.importSchedule.dataSource);
        }
      }
    });
    this.searchResultsRef.hide();
  }


  createImportItemDetailsFromSearchResults(searchResults: ImportSearchResults, dataSource: string, node: PortInfo): ImportItemDetails {
    var importItemDetails: ImportItemDetails = {
      nodeName: (searchResults.nodeName != undefined) ? searchResults.nodeName : '',
      nodeType: (searchResults.nodeType != undefined) ? searchResults.nodeType : '',
      port_number: 0,
      product_name: '',
      manufacturer: '',
      lsid: 0,
      crop: WPCrop.corn,
      useLocalTime: 'true',
      city: searchResults.city,
      region: searchResults.region,
      country: searchResults.country,
      gateway_Type: searchResults.gateway_Type,
      station_Id_Uuid: searchResults.station_Id_Uuid,
      user_Email: searchResults.user_Email,
      subscription_Type: searchResults.subscription_Type
    };

    if (dataSource == 'Weather Pillar' && node != undefined) {
      importItemDetails.port_number = node.port_number;
      importItemDetails.product_name = node.product_name;
      importItemDetails.manufacturer = node.manufacturer;
      importItemDetails.lsid = node.lsid;
    }
   
    return importItemDetails;
  }


  compareSyncMcSensors(importItem: ImportItem, saveSync: boolean, template: TemplateRef<any>) {
    importItem.isLoadingDataTypes = true;
    this.deviceClass = importItem.thingName;

    this._importService.compareSyncMcSensors(this.deviceClass, saveSync).subscribe(
      results => {
        if (results != null) {
          importItem.compareSyncResults = results;

          if (importItem.compareStats == null) importItem.compareStats = new CompareMcStats();

          importItem.compareStats.kigNew = importItem.compareSyncResults.sensors.filter(x => x.kigNew == true).length;
          importItem.compareStats.kigNameUpdate = importItem.compareSyncResults.sensors.filter(x => x.kigNameUpdate == true).length;
          importItem.compareStats.kigProdIdUpdate = importItem.compareSyncResults.sensors.filter(x => x.kigProdIdUpdate == true).length;
          importItem.compareStats.kigGroupUpdate = importItem.compareSyncResults.sensors.filter(x => x.kigGroupUpdate == true).length;
          importItem.compareStats.particleNew = importItem.compareSyncResults.sensors.filter(x => x.particleNew == true).length;
          importItem.compareStats.particleNameUpdate = importItem.compareSyncResults.sensors.filter(x => x.particleNameUpdate == true).length;
          importItem.compareStats.particleProdIdUpdate = importItem.compareSyncResults.sensors.filter(x => x.particleProdIdUpdate == true).length;
          importItem.compareStats.particleGroupUpdate = importItem.compareSyncResults.sensors.filter(x => x.particleGroupUpdate == true).length;

          importItem.compareStats.syncNeeded = (importItem.compareStats.kigNew > 0 || importItem.compareStats.kigNameUpdate > 0 || importItem.compareStats.kigProdIdUpdate > 0 ||
                                                importItem.compareStats.kigGroupUpdate > 0 ||importItem.compareStats.particleNew > 0 || importItem.compareStats.particleNameUpdate > 0 ||
                                                importItem.compareStats.particleProdIdUpdate > 0 || importItem.compareStats.particleGroupUpdate > 0) ? true : false;

          if (template != undefined) {
            this.currentImportItem = importItem;
            this.sensorResultsRef = this.modalService.show(template, { animated: false, backdrop: false, ignoreBackdropClick: true } );
          }

          if (saveSync) {
            this.compareSyncMcSensors(importItem, false, null);
            this._sensorGroupService.clearCachedVariables();
          }
        } else {
          this.toastr.warning('Error comparing and/or sync\'ing sensor parameters');
        }

        importItem.isLoadingDataTypes = false;
        return;
      },
      err => {
        this.toastr.error('Error occurred while comparing and/or sync\'ing sensor parameters');
        importItem.isLoadingDataTypes = false;
    });
  }


  syncMcSensor(sensor: SensorBasicsForCompareSync) {
    //var deviceClass = this.currentImportItem.thingName;

    this._importService.syncMcSensor(sensor.uid).subscribe(
      results => {
        if (results != null) {
          this.compareSyncMcSensors(this.currentImportItem, false, null);   // refresh list
          this._sensorGroupService.clearCachedVariables();
        } else {
          this.toastr.warning('Error syncing sensor parameters');
        }

        return;
      },
      err => {
        this.toastr.error('Error occurred while syncing sensor parameters');
    });
  }


  getDataTypesForThing(importItem: ImportItem, dataSource: string) {
    var endDate = moment(this.sensorReadingTypesStartDate).endOf('day').format('YYYY-MM-DD HH:mm');
    var startDate = moment(this.sensorReadingTypesStartDate).startOf('day').format('YYYY-MM-DD HH:mm');

    var dataSearchCriteria: ImportDataCriteria = {
      thingId: importItem.thingName,
      refId: importItem.refId,
      dataSource: dataSource,
      startDate: startDate,
      endDate: endDate,
      dataTypes: [],
      timeStampParm: 'timestamp',
      feed: Object.values(ImportFeed).indexOf(importItem.feed),
      latitudeDaily: importItem.latitudeDaily,
      longitudeDaily: importItem.longitudeDaily,
      radiusDaily: importItem.radiusDaily,
      thingName: importItem.thingName,
      details: JSON.stringify(importItem.details),
      thingKey: importItem.key,
      thingSecret: "",
      timeZoneDisplay: this.selectedTimeZone,
      timeZoneOffset: this.selectedTimeZoneOffset,
    }

    try {
      var keyObj = JSON.parse(importItem.key);
      dataSearchCriteria.thingKey = keyObj.key;
      dataSearchCriteria.thingSecret = keyObj.secret;
    } catch { }

    importItem.isLoadingDataTypes = true;

    this._importService.getDataTypesForThing(dataSearchCriteria).subscribe(
      results => {
        if (results != null) {

          if (Array.isArray(results) == false) {
            this.toastr.error('Error searching for data types');
            this.toastr.error(results.toString());
            importItem.isLoadingDataTypes = false;
            return;
          }

          var retrievedDataTypeOptions: string[] = results;
          this.common.sortStringArray(retrievedDataTypeOptions);
          importItem.dataTypeOptions = [];
          retrievedDataTypeOptions.forEach(x => {
            importItem.dataTypeOptions.push( { id: this.generateRandomPositiveID(), name: x } );
          });
          importItem.dataTypeOptions = [...importItem.dataTypeOptions];
        } else {
          this.toastr.warning('Error searching for data');
        }
        importItem.isLoadingDataTypes = false;
        return;
      },
      err => {
        this.toastr.error('Error occurred while search for reading types');
        this.toastr.error(JSON.stringify(err.error));
    });
  }


  previewImportItemData(importItem: ImportItem, dataSource: string, template: TemplateRef<any>) {
    this.selectedDateRange = { startDate: moment(today).startOf('day'), endDate: moment(today).endOf('day') };

    var today = moment().format("YYYY-MM-DD");
    var endDate = moment(today).endOf('day').format('YYYY-MM-DD HH:mm');
    var startDate = moment(today).add(-1, 'day').startOf('day').format('YYYY-MM-DD HH:mm');

    this.currentImportItem = importItem;

    var importDataCriteria: ImportDataCriteria = {
      thingId: importItem.thingName,
      refId: importItem.refId,
      dataSource: dataSource,
      startDate: startDate,
      endDate: endDate,
      dataTypes: importItem.dataTypesSelected.map(x => x.name),
      timeStampParm: importItem.timeStampParm.name,
      feed: Object.values(ImportFeed).indexOf(importItem.feed),
      latitudeDaily: importItem.latitudeDaily,
      longitudeDaily: importItem.longitudeDaily,
      radiusDaily: importItem.radiusDaily,
      thingName: importItem.thingName,
      details: JSON.stringify(importItem.details),
      thingKey: importItem.key,
      thingSecret: "",
      timeZoneDisplay: this.selectedTimeZone,
      timeZoneOffset: this.selectedTimeZoneOffset
    }

    try {
      var keyObj = JSON.parse(importItem.key);
      importDataCriteria.thingKey = keyObj.key;
      importDataCriteria.thingSecret = keyObj.secret;
    } catch { }

    this.getDataForThing(importDataCriteria, template);
  }


  getDataForThing(importDataCriteria: ImportDataCriteria, template: TemplateRef<any>) {
    if (this.getDataForThingLocked == true) {
      return;
    }

    this._importService.getDataForThing(importDataCriteria).subscribe(
      results => {
        if (results != null) {

          if (Array.isArray(results) == false) {
            this.toastr.error('Error searching for readings');
            this.toastr.error(results.toString());
            return;
          }

          this.importDataPreviewResults = results;
          if (template != null) {
            this.getDataForThingLocked = true;
            this.dataPreviewResultsRef = this.modalService.show(template, { animated: false, backdrop: false, ignoreBackdropClick: true } );
            setTimeout(() => {
              this.getDataForThingLocked = false;
            }, 1000);
          }
        } else {
          this.toastr.warning('Error searching for data');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while search for readings');
        this.toastr.error(JSON.stringify(err.error));
    });
  }


  importNowDatesUpdated() { }


  manualImport(template: TemplateRef<any>) {
    this.importNowCount = "";
    this.selectedDateRange.startDate = undefined;
    this.selectedDateRange.endDate = undefined;
    this.scheduleImportNowRef = this.modalService.show(template, { animated: false, backdrop: false, ignoreBackdropClick: true } );
  }


  importNow() {
    this.updateSensorName(this.importSchedule);

    var startYMD = this.selectedDateRange.startDate.format("YYYY-MM-DD");
    var endYMD = this.selectedDateRange.endDate.format("YYYY-MM-DD");
    var startDate = moment(startYMD).startOf('day').local().toDate();
    var endDate = moment(endYMD).endOf('day').local().toDate();

    var importNowCriteria: ImportNowCriteria = {
      endDate: endDate,
      startDate: startDate,
      importScheduleInfo: this.convertImportSchedule2ImportScheduleInfo(this.importSchedule),
      currentDate: moment().toDate()
    }
    this.importNowIsLoading = true;
    this.importNowCount = "";

    this._importService.importNow(importNowCriteria).subscribe(
      results => {
        this.importNowIsLoading = false;
        var importQueueResults: { [key: string] : string } = results;

        if (importQueueResults["status"] != undefined && importQueueResults["status"] == "success") {
          //this.importNowCount = `Job queued. Queue Size (${importQueueResults["queuesize"]})`;
          this.importNowCount = `Job queued`;
        }
        else {
          this.importNowCount = "Error occourred with reply. Check console.";
          this.toastr.warning('Error importing data');
        }
      },
      err => {
        this.importNowCount = "Error occourred. Check console.";
        this.importNowIsLoading = false;
        this.toastr.error('Error occurred while importing data');
    });
  }


  isInvalidDate = (m: moment.Moment) => this.invalidDates.some(d => d.isSame(m, 'day'));


  datesUpdated() {
    if (this.selectedDateRange == undefined ||
        this.selectedDateRange.startDate == undefined ||
        this.selectedDateRange.endDate == undefined) {
      return;
    }

    var startYMD = this.selectedDateRange.startDate.format("YYYY-MM-DD");
    var endYMD = this.selectedDateRange.endDate.format("YYYY-MM-DD");
    var startDate = moment(startYMD).startOf('day').format('YYYY-MM-DD HH:mm');
    var endDate = moment(endYMD).endOf('day').local().format('YYYY-MM-DD HH:mm');

    var dataSearchCriteria: ImportDataCriteria = {
      thingId: this.currentImportItem.thingName,
      refId: this.currentImportItem.refId,
      dataSource: this.importSchedule.dataSource,
      startDate: startDate,
      endDate: endDate,
      dataTypes: this.currentImportItem.dataTypesSelected.map(x => x.name),
      timeStampParm: this.currentImportItem.timeStampParm.name,
      feed: Object.values(ImportFeed).indexOf(this.currentImportItem.feed),
      latitudeDaily: this.currentImportItem.latitudeDaily,
      longitudeDaily: this.currentImportItem.longitudeDaily,
      radiusDaily: this.currentImportItem.radiusDaily,
      thingName: this.currentImportItem.thingName,
      details: JSON.stringify(this.currentImportItem.details),
      thingKey: this.currentImportItem.key,
      thingSecret: "",
      timeZoneDisplay: this.selectedTimeZone,
      timeZoneOffset: this.selectedTimeZoneOffset
    }

    try {
      var keyObj = JSON.parse(this.currentImportItem.key);
      dataSearchCriteria.thingKey = keyObj.key;
      dataSearchCriteria.thingSecret = keyObj.secret;
    } catch { }

    this.getDataForThing(dataSearchCriteria, undefined);
  }


  cloneSchedule(schedule: ImportSchedule): ImportSchedule {
    const newSchedule: ImportSchedule = JSON.parse(JSON.stringify(schedule));
    newSchedule.modifiedDate = schedule.modifiedDate;
    return newSchedule;
  }


  addImportItem(thingId: string, thingName: string, latitude: number, longitude: number, key: string, details: ImportItemDetails): ImportItem {
    if (details == undefined) {
      details = {
        nodeName: '',
        nodeType: '',
        port_number: 0,
        product_name: '',
        manufacturer: '',
        lsid: 0,
        crop: WPCrop.corn,
        useLocalTime: 'true',
        city: '',
        region: '',
        country: '',
        gateway_Type: '',
        station_Id_Uuid: '',
        user_Email: '',
        subscription_Type: ''
      };
    }

    var newImportItem: ImportItem = {
      id: this.generateRandomNegativeID(),
      thingName: thingName,
      details: details,
      dataTypesSelected: [],
      dataTypeOptions: [],
      importScheduleId: this.importSchedule.id,
      timeStampParm: { id: 0, name: 'timestamp' },
      feed: (this.importSchedule.dataSource.toLowerCase() === 'weather link') ? ImportFeed.Current : ImportFeed.SubDaily,
      latitudeDaily: latitude,
      longitudeDaily: longitude,
      radiusDaily: 10,
      refId: thingId,
      key: key,
      compareSyncResults: undefined,
      compareStats: undefined
    }
    this.initializeCompareSyncStatResults(newImportItem);

    this.importSchedule.importItems.push(newImportItem);
    return newImportItem;
  }

  
  initializeCompareSyncStatResults(importItem: ImportItem) {
    importItem.compareSyncResults = new CompareSyncResults();
    importItem.compareSyncResults.sensors = [];
    importItem.compareStats = new CompareMcStats();
  }


  copyImportItem(importItem: ImportItem) {
    var newImportItem = this.addImportItem('', importItem.thingName, 0, 0, importItem.key, importItem.details);
    newImportItem.dataTypesSelected = [...importItem.dataTypesSelected];
    newImportItem.dataTypeOptions = [...importItem.dataTypeOptions];
    newImportItem.timeStampParm = importItem.timeStampParm;
  }


  delImportItem(importItem: ImportItem) {
    var itemIndex = this.importSchedule.importItems.findIndex(x => x.id == importItem.id);
    this.importSchedule.importItems.splice(itemIndex, 1);
    this.importSchedule.importItems = [...this.importSchedule.importItems];
  }


  generateRandomNegativeID(): number {
    return Math.floor(Math.random() * 20000) - 20001;
  }


  generateRandomPositiveID(): number {
    return Math.floor(Math.random() * 30000);
  }


  createMessageData(msgActionType: MessageActionType, schedule: ImportSchedule, msgNote: string) {
    const message: Message = {
      id: 0,
      messageType: MessageType.Audit,
      appComponentName: AppComponentName.Imports,
      actionType: msgActionType,
      appComponentItem: (schedule != undefined) ? schedule.name : '',
      details: msgNote,
      userNotes: '', //this.changeLogNotes,
      count: 1,
      acknowledged: false,
      userName: this.username,
      userGroupId: (schedule != undefined) ? schedule.userGroupId : null
    };

    this._appMessageService.createAppMessage(message).subscribe(
      results => {
        if (results != null) {
          //this.toastr.success('Success');
        } else {
          //this.toastr.warning('Not found.');
        }
        return;
      },
      err => {
        //this.toastr.error('Error occurred while saving change log.');
      });
  }


  getEditChanges(schedule: ImportSchedule): string {
    let results = '';

    if (this.preEditObjectAsJSONString === '') {
      return results;
    }

    const original: ImportSchedule = JSON.parse(this.preEditObjectAsJSONString);

    results += this.common.compareProperty('Name', original.name, schedule.name);
    results += this.common.compareProperty('Description', original.description, schedule.description);
    results += this.common.compareProperty('Enabled', this.common.prepareProperty4String(!original.isDisabled), this.common.prepareProperty4String(!schedule.isDisabled));
    results += this.common.compareProperty('UserGroupID', this.common.prepareProperty4String(original.userGroupId), this.common.prepareProperty4String(schedule.userGroupId));
    results += this.common.compareProperty('Data Source', original.dataSource, schedule.dataSource);
    results += this.common.compareProperty('Frequency', original.frequency, schedule.frequency);
    results += this.common.compareProperty('FreqValue', this.common.prepareProperty4String(original.freqValue), this.common.prepareProperty4String(schedule.freqValue));

    if (original.importItems.length !== schedule.importItems.length) {
      results += this.common.compareProperty('Import Items', original.importItems.length.toString(), schedule.importItems.length.toString());
    }

    return results;
  }


  showDataPreviewChart(template: TemplateRef<any>) {
    if (this.importDataPreviewResults.length < 1) {
      return;
    }

    this.dataTypes = [];

    var dataTypesAsStrings = this.importDataPreviewResults.map(x => x.dataType);
    dataTypesAsStrings = [... new Set(dataTypesAsStrings)];

    for (var i = 0; i< dataTypesAsStrings.length; i++) {
      var x = dataTypesAsStrings[i];
      var sensorReadingType: SensorReadingType = {
        id: i,
        type: x,
        checked: (i == 0) ? true : false
      }
      this.dataTypes.push(sensorReadingType);
    }

    this.updateChartData();

    this.chartModalRef = this.modalService.show(template, { animated: false, backdrop: false, ignoreBackdropClick: false });
  }


  updateChartData() {
    this.chart = [];

    this.dataTypes.forEach(x => {
      if (x.checked) {
        var dataSeries = this.importDataPreviewResults.filter(s => s.dataType === x.type && s.value != "nan" && s.value != "NaN")
                                                      .map(a => ({ name: new Date(a.timeStamp), value: a.value }));

        var newData = {
          name: x.type,
          series: dataSeries,
          uuid: this.importDataPreviewResults[0].thingId
        };

        if (newData.series.length > 0) {
          this.chart.push(newData);
          this.chart = [...this.chart];
        }
      }
    });
  }


  selectCSVFile(fileEvent) {
    this.importSchedule.frequency = ImportFrequency.Once;
    this.loadCSVFile(fileEvent);
  }


  loadCSVFile(fileEvent) {
    const previewRecordCount = 300;

    this.csvFilename = '';
    this.csvFileContent = [];
    this.csvFileContentShown = [];
    this.importCSVFileAnalysisResults = "";
    this.errorAnalyzingCSVFile = false;
    this.csvFileAnalysisResults = {
      records: 0,
      uniqueUUIDs: 0,
      newUUIDs: 0,
      recordsWithGPS: 0,
      existingRecords: 0,
      numNewReadingTypes: 0,
      recordsImported: 0
    };

    const fileReader = new FileReader();
    fileReader.onload = (content: any) => {
      var records: string[] = content.target.result.split('\n');
      records.forEach(x => {
        var record = x.replace(/\r/g, '')

        if (record.length > 0) {
          var fields = record.split(',').map(field => field.trim());
          this.csvFileContent.push(fields);
          if (this.csvFileContent.length < previewRecordCount) {
            this.csvFileContentShown.push(fields);
          }
        }
      });

      var convertCSVFileForAnlysis = this.convertCSVFileForAnlysis(this.csvFileContent);

      if (this.preCheckCSVContentAnalysis(convertCSVFileForAnlysis)) {
        this.analyzeFileContent(convertCSVFileForAnlysis);
      }

    };

    if (fileEvent.target.files.length > 0) {
      this.analyzingCSVFile = true;
      this.csvFilename = fileEvent.target.files[0].name;
      fileReader.readAsText(fileEvent.target.files[0]);
    }
  }


  preCheckCSVContentAnalysis(convertCSVFileForAnlysis: ImportCSVFileContent[]): boolean {
    this.analyzingCSVFile = true;

    if (this.csvFileContent.length < 2) {
      this.toastr.error('Error: Can not import. Must have a header row and at least one data row');
      this.importCSVFileAnalysisResults = "Error: Can not import. Must have a header row and at least one data row.";
      this.analyzingCSVFile = false;
      return false;
    }

    var missingFields = this.validateRequiredFields(this.csvFileContent[0]).join(', ');
    if (missingFields != '') {
      this.toastr.error('Error: First row missing required fields (' + missingFields + ')');
      this.importCSVFileAnalysisResults = 'Error: First row missing required fields (' + missingFields + ')';
      this.analyzingCSVFile = false;
      return false;
    }

    var inValidCreateDateCount = 0;
    convertCSVFileForAnlysis.forEach(x => {
      if (!(x.createdDate instanceof Date) || isNaN(x.createdDate.getMinutes())) {
        inValidCreateDateCount++;
      }
    });
    if (inValidCreateDateCount > 0) {
      this.toastr.error(`Error: Invalid CreatedDates (${inValidCreateDateCount} of ${convertCSVFileForAnlysis.length} records)`);
      this.importCSVFileAnalysisResults = `Error: Invalid CreatedDates (${inValidCreateDateCount} of ${convertCSVFileForAnlysis.length} records)`;
      this.analyzingCSVFile = false;
      return false;
    }

    return true;
  }


  analyzeFileContent(convertCSVFileForAnlysis: ImportCSVFileContent[]) {
    //var convertCSVFileForAnlysis = this.convertCSVFileForAnlysis(this.csvFileContent);
    this.parsedCSVFileContent = [];

    const chunkSize = 5000;
    for (let i = 0; i < convertCSVFileForAnlysis.length; i += chunkSize) {
      const chunk = convertCSVFileForAnlysis.slice(i, i + chunkSize);

      this._importService.analyzeCSVFileContent(chunk).subscribe(
        results => {
          if (results != null) {
            this.parsedCSVFileContent = this.parsedCSVFileContent.concat(results);
          } else {
            this.toastr.warning('Error analyzing file chunk');
          }

          if (this.parsedCSVFileContent.length == convertCSVFileForAnlysis.length) {
            this.analyzingCSVFile = false;
            this.displayAnalysisResults(this.parsedCSVFileContent);
          }
          return;
        },
        err => {
          this.analyzingCSVFile = false;
          this.errorAnalyzingCSVFile = true;
          this.toastr.error('Error occurred while analyzing file chunk');
      });
    }
  }


  displayAnalysisResults(importFileContent: ImportCSVFileContent[]) {
    var uniqueUUIDs: string[] = [];
    importFileContent.forEach(x => uniqueUUIDs.push(x.uuid));
    var uniqueUUIDs = [... new Set(uniqueUUIDs)];

    var recordsWithGPS = importFileContent.filter(x => x.validGPSInfo == true).length;
    var existingRecords = importFileContent.filter(x => x.recordExists == true).length;

    var newUUIDs: string[] = [];
    importFileContent.forEach(x => {
      if (x.newUUID == true) newUUIDs.push(x.uuid);
    });
    newUUIDs = [... new Set(newUUIDs)];

    this.csvFileAnalysisResults = {
      records: importFileContent.length,
      uniqueUUIDs: uniqueUUIDs.length,
      newUUIDs: newUUIDs.length,
      recordsWithGPS: recordsWithGPS,
      existingRecords: existingRecords,
      numNewReadingTypes: importFileContent[0].numNewReadingTypes,
      recordsImported: this.csvFileAnalysisResults.recordsImported
    }
  }


  readyToImportFileContent(): boolean {
    if (this.csvFileContent.length < 2)
      return false;

    var missingFields = this.validateRequiredFields(this.csvFileContent[0]).join(', ');
    if (missingFields != '')
      return false;

    if (this.csvFileAnalysisResults.recordsImported > 0)
      return false;

    if (this.errorAnalyzingCSVFile)
      return false;

    return true;
  }


  validateRequiredFields(headerRecord: string[]): string[] {
    var results: string[] = [];

    var searchItems = [ 'uuid', 'tag', 'createddate' ];

    searchItems.forEach(s => {
      if (headerRecord.findIndex(x => x.toLowerCase() == s.toLowerCase()) == -1) {
        results.push(s);
      }
    });

    if (headerRecord.length <= searchItems.length) {
      results.push('<key field>')
    };

    return results;
  }


  convertCSVFileForAnlysis(delimintedFileContent: string[][]): ImportCSVFileContent[] {
    var importCSVFileContent: ImportCSVFileContent[] = [];
    var header: string[] = [...delimintedFileContent[0]];
    header = header.map(x => x.toLowerCase());

    for (var index = 1; index < delimintedFileContent.length; index++) {
      var record: ImportCSVFileContent = {
        uuid: '',
        createdDate: undefined,
        readingTypes: [],
        newUUID: false,
        recordExists: false,
        missingRequiredField: false,
        validGPSInfo: true,
        tag: '',
        gpsLong: 0,
        gpsLat: 0,
        gpsAlt: 0,
        locationDescription: '',
        numNewReadingTypes: 0
      };

      for (var col=0; col < header.length; col++) {
        var field = delimintedFileContent[index][col];
        switch (header[col]) {
          case 'uuid':
            record.uuid = field.toLowerCase();
            break;

          case 'tag':
            record.tag = field;
            break;

          case 'createddate':
            record.createdDate = moment(field).toDate();
            break;

          case 'gpslong':
            record.gpsLong = Math.abs(Number(field)) <= 180 ? Number(field) : 0;
            break;

          case 'gpslat':
            record.gpsLat = Math.abs(Number(field)) <= 90 ? Number(field) : 0;
            break;

          case 'gpsalt':
            record.gpsAlt = Number(field);
            break;

          case 'locationdescription':
            record.locationDescription = field;
            break;

          default:
            record.readingTypes.push({ type: header[col], value: field, isNewReading: true })
            break;
        };
      }

      if (record.uuid == '' || record.createdDate == undefined || record.readingTypes.length < 1) {
        record.missingRequiredField = true;
      }

      if (record.gpsLong > 0 || record.gpsLat > 0 || record.gpsAlt > 0) {
        record.validGPSInfo = true;
      }

      importCSVFileContent.push(record);

    };

    return importCSVFileContent;
  }


  getRecordColor(index: number): string {
    if (index == 0) {
      return 'black';
    }
    else if (this.parsedCSVFileContent[index-1] != undefined && this.parsedCSVFileContent[index-1].newUUID == true) {
      return 'green';
    }
    else if (this.parsedCSVFileContent[index-1] != undefined && this.parsedCSVFileContent[index-1].recordExists) {
      return 'red';
    }
    else {
      return 'black';
    }
  }


  // Importing bulk involves
  // 1. Importing first record
  // 2. Importing first record of each new unique uuid - to minimize risk of duplicating reading types
  // 3. Importing all of the rest of the records

  importCSVFileContent() {
    this.importFirstRecordToCreateNewReadingTypes();

    var msg = `Initiated CSV data import from \'${this.csvFilename}\' with approx ${this.parsedCSVFileContent.length - this.csvFileAnalysisResults.existingRecords} records`;
    this.createMessageData(MessageActionType.New, undefined, msg);
  }


  importFirstRecordToCreateNewReadingTypes() {
    var firstRecord = [this.parsedCSVFileContent[0]];

    this._importService.bulkCSVImport(firstRecord).subscribe(
      results => {
        this.csvFileAnalysisResults.recordsImported += Number(results);
        this.setRecordToReadingsExist(firstRecord);
        this.importNewUniqueUUIDs();
        return;
      },
      err => {
        this.toastr.error('Error occurred while importing data');
    });
  }


  importNewUniqueUUIDs() {
    var uuids = this.parsedCSVFileContent.map(x => x.uuid);
    var uuids = [... new Set(uuids)];

    var oneRecordOfEachUUID: ImportCSVFileContent[] = [];
    uuids.forEach(x => {
      var record = this.parsedCSVFileContent.find(y => x.includes(y.uuid));
      if (record != undefined) {
        oneRecordOfEachUUID.push(record);
      }
    });

    this._importService.bulkCSVImport(oneRecordOfEachUUID).subscribe(
      results => {
        this.csvFileAnalysisResults.recordsImported += Number(results);
        this.setRecordToReadingsExist(oneRecordOfEachUUID);
        this.importBulkContent();
        return;
      },
      err => {
        this.toastr.error('Error occurred while importing data');
    });
  }


  importBulkContent() {
    const chunkSize = 100;
    for (let i = 0; i < this.parsedCSVFileContent.length; i += chunkSize) {
      const chunk = this.parsedCSVFileContent.slice(i, i + chunkSize);

      this._importService.bulkCSVImport(chunk).subscribe(
        results => {
          this.csvFileAnalysisResults.recordsImported += Number(results);
          return;
        },
        err => {
          this.toastr.error('Error occurred while importing data');
      });
    }
  }


  setRecordToReadingsExist(records: ImportCSVFileContent[]) {
    records.forEach(x => {
      x.readingTypes.forEach(y => y.isNewReading = false);
    });
  }


  displaySampleCSVFormat(template: TemplateRef<any>) {
    this.sampleCSVFormatRef = this.modalService.show(template, { animated: false, backdrop: false, ignoreBackdropClick: false } );
  }


  showSoiltechReadingTypeKey(template: TemplateRef<any>) {
    this.readingTypeKeyRef = this.modalService.show(template, { animated: false, backdrop: false, ignoreBackdropClick: false } );
  }


  createSvgForDataAvailability(): void {
    this.svgDataAvailability = d3.select("figure#bar")
    .append("svg")
    .attr("width", this.dataAvailabilityWidth + (this.dataAvailabilityMargin * 2))
    .attr("height", this.dataAvailabilityHeight + (this.dataAvailabilityMargin * 2))
    .append("g")
    .attr("transform", "translate(" + this.dataAvailabilityMargin + "," + this.dataAvailabilityMargin + ")");
  }


  drawDataAvailabilityBars(): void {
    var yearData = this.sensorDataAvailability.map(x => Number(moment(x).format("YYYY")) + moment(x).dayOfYear()/365);
    var years = this.sensorDataAvailability.map(x => Number(moment(x).format("YYYY")));
    var minYear = Math.floor(Math.min(...years));
    var maxYear = Math.floor(Math.max(...years)) + 1;
   
    // Add X axis
    const x = d3.scaleLinear()
      .domain([minYear, maxYear])
      .range([ 0, this.dataAvailabilityWidth ]);

    this.svgDataAvailability.append("g")
      .attr("transform", "translate(0," + this.dataAvailabilityHeight + ")")
      .call(d3.axisBottom(x).tickFormat(d3.format("d")).ticks(maxYear - minYear));

    // Add Y axis
    const y = d3.scaleLinear()
      .domain([0, 2])
      .range([ this.dataAvailabilityHeight, 0]);

    // Create and fill the bars
    this.svgDataAvailability.selectAll("bars")
      .data(yearData)
      .enter()
      .append("rect")
      .attr("x", d => x(d))
      .attr("y", d => y(2))
      .attr("width", this.dataAvailabilityWidth/((maxYear - minYear + 1) * 365))
      .attr("height", (d) => this.dataAvailabilityHeight - y(1.8))
      .attr("fill", "#548235");
  }


  feedSelectionChanged(importItem: ImportItem, dataSource: string) {
    this.updateImportItemTimeStamp(importItem);
    this.getDataTypesForThing(importItem, dataSource);
  }


  updateImportItemTimeStamp(importItem: ImportItem) {
    if (this.importSchedule.dataSource == "Weather Pillar") {
      if (importItem.feed != ImportFeed.SubDaily) {
        importItem.timeStampParm = { id: 0, name: "date" };
      }
      else {
        importItem.timeStampParm = { id: 0, name: "timestamp" };
      }
    }
    else if (this.importSchedule.dataSource == "SenTek") {
      importItem.timeStampParm = { id: 0, name: "Date Time" };
    }
    else if (this.importSchedule.dataSource == "IRROCloud") {
      importItem.timeStampParm = { id: 0, name: "datetime" };
    }
  }


  previewReady(importItem: ImportItem): boolean {
    if (importItem.feed == ImportFeed.SubDaily || importItem.feed == ImportFeed.DailyObsSta) {  // importItem.feed == WPImportFeed.DailyAvgSta || 
      return (importItem.thingName != '' && importItem.dataTypesSelected.length > 0);
    }
    else {
      return (importItem.latitudeDaily != 0 && importItem.longitudeDaily != 0 && importItem.radiusDaily != 0 && importItem.dataTypesSelected.length > 0);
    }
  }


  updateSensorName(importSchedule: ImportSchedule) {
    importSchedule.importItems.forEach(x => {
      if (x.feed == ImportFeed.DailyAvgLL || x.feed == ImportFeed.DailyObsLL) {
        x.thingName = x.latitudeDaily.toString() + "_" + x.longitudeDaily.toString() + "_" + x.radiusDaily.toString();
      }
    });
  }


  showDataPointsOnChart(chart) {
    this.charting.showDataPointsOnChart(chart);
  }


  selectedTimeZoneChanged() {
    switch (this.selectedTimeZone) {
      case TimeZoneSelection.Local:
        localStorage.setItem('TimeZoneSelected', 'Local');
        this.selectedTimeZoneOffset = moment().utcOffset();
        break;
    
    case TimeZoneSelection.UTC:
      localStorage.setItem('TimeZoneSelected', 'UTC');
        this.selectedTimeZoneOffset = 0;
        break;

    case TimeZoneSelection.Source:
      localStorage.setItem('TimeZoneSelected', 'Source');
        this.selectedTimeZoneOffset = 0;
        break;

      default:
        this.selectedTimeZoneOffset = 0;
        break;
    }

    this.datesUpdated();
  }


  openImportLogs(importSchedule: ImportSchedule): void {
    const params: ImportLogsModalParams = {
      importSchedule: importSchedule,
    };
    this.modalService.show(ImportLogsModalComponent, { ...this.logConfig, initialState: params });
  }


  // For testing
  importAll() {
    this._importService.importAll().subscribe(
      results => {
        this.toastr.success('Import job queued successfully');
        return;
      },
      err => {
        console.error(err);
        this.toastr.error('Error occurred while importing data');
    });
  }


}
